import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    this.#resizeObserver = new ResizeObserver(() => {
      this.#handleResize();
    });
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    window.removeEventListener('resize', this.#handleResize);
  }

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  /**
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  /**
   * Min segments to fill the bar. Uses segment width only; +2 covers flex `gap` between segments
   * so we do not under-fill after wide viewports or resize.
   * @param {number} containerWidth
   * @param {number} segmentWidth
   */
  #computeNeededCopies(containerWidth, segmentWidth) {
    if (segmentWidth <= 0) return 2;
    const raw = Math.ceil(containerWidth / segmentWidth) + 2;
    return Math.max(2, raw);
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 2, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            const numberOfCopies = this.#computeNeededCopies(marqueeWidth, marqueeItemsWidth);
            resolve({
              numberOfCopies,
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * Read dimensions after layout (for resize). Extra rAF beats reading stale width before expand reflow.
   */
  #getDimensionsAfterResize() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const { marqueeItems } = this.refs;
            const marqueeWidth = this.getBoundingClientRect().width;
            const marqueeItemsWidth = marqueeItems[0] ? marqueeItems[0].getBoundingClientRect().width : 0;
            this.#marqueeWidth = marqueeWidth;
            resolve({
              newNumberOfCopies: this.#computeNeededCopies(marqueeWidth, marqueeItemsWidth),
            });
          });
        });
      });
    });
  }

  /**
   * Duration (seconds) for one full scroll cycle. Linear in numberOfCopies so pixel speed is
   * consistent across viewport sizes.
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    const speed = numberOfCopies * speedFactor;
    return Math.max(40, speed);
  }

  #handleResize = debounce(async () => {
    const { newNumberOfCopies } = await this.#getDimensionsAfterResize();

    const { content } = this.refs;
    if (!content?.firstElementChild) return;

    // Use live DOM count — stale `this.refs.marquee` from a closure can be wrong after ref updates.
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies === currentNumberOfCopies) return;

    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      const delta = newNumberOfCopies - currentNumberOfCopies;
      const template = content.firstElementChild;
      for (let i = 0; i < delta; i++) {
        content.appendChild(/** @type {HTMLElement} */ (template.cloneNode(true)));
      }
    } else {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    // Do not restart animation on resize — avoids visible jump; scroll continues from current position
  }, 300);

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
